// Mobile Payment Approvals: turns the API's WebPaymentApproval into what the cards, the
// filters and the Details page show. No Angular here, so it can be tested on its own.
//
// Two things the API does not say, and how they are worked out:
//  - Which approver and which comments are the current user's. Approvers and comment
//    authors carry the approver's friendly name only, so they are matched against the
//    user's own names (isMe). A payment the user queried earlier in the session is also
//    passed in (queriedIds), for the time before the name has been learnt.
//  - The invoice date. There is none, so DateCaptured is used, as the old cards did.
//
// The list has two tabs (TABS), as desktop has them: Active (in the code, new), every payment
// still being approved or queried (the server's approvals list), and History, the fully
// approved and paid ones (the server's payment search, a page at a time).

// What the search, filter and sort controls offer on each tab. History's search, like
// desktop's, is the server's (HISTORY_SEARCH), with no filter or sort of its own.
export const TABS = {
    new: {
        placeholder: 'Search supplier or invoice',
        emptyText: 'No payments awaiting your approval.',
        defaultSort: 'newest',
        statusFilters: [
            { value: 'all', label: 'All' },
            { value: 'awaiting', label: 'Awaiting you' },
            { value: 'queried', label: 'Queried' },
            // Approved by the user, others still to approve: it stays in Active until it is fully approved
            { value: 'approved', label: 'You approved' },
            { value: 'others', label: 'Not yours' }
        ],
        sortOptions: [
            { value: 'newest', label: 'Newest first' },
            { value: 'oldest', label: 'Oldest first' },
            { value: 'amount-desc', label: 'Amount: high to low' },
            { value: 'amount-asc', label: 'Amount: low to high' }
        ]
    },
    history: {
        placeholder: 'Search history',
        emptyText: 'No payment history yet. Payments appear here once they have been fully approved.'
    }
};

// History's search, as desktop's (the server's SearchOptions: 1 invoice, 2 supplier, 3 amount):
// one option at a time, with desktop's rules for when Search can be pressed (historySearchHint)
export const HISTORY_SEARCH = [
    { value: 'invoice', option: 1, label: 'Invoice' },
    { value: 'supplier', option: 2, label: 'Supplier' },
    { value: 'amount', option: 3, label: 'Amount' }
];

// Desktop's page sizes, and its default
export const HISTORY_PAGE_SIZES = [5, 10, 15, 20];
export const HISTORY_DEFAULT_PAGE_SIZE = 5;

/**
 * Why History's search cannot run yet, as desktop's searchHint says it, or '' when it can.
 * Nothing typed and no option runs the plain list; a term needs an option, a supplier at
 * least 3 letters, and an amount a number.
 * @param {string} term What is typed
 * @param {string|null} option One of HISTORY_SEARCH's values, or null
 * @return {string} The hint, or '' when Search can be pressed
 */
export function historySearchHint(term, option) {
    const text = (term || '').trim();
    if (!option) {
        return text ? 'Choose what to search by: invoice, supplier or amount.' : '';
    }
    if (!text) {
        return 'Type what you are looking for.';
    }
    if (option === 'supplier' && text.length < 3) {
        return 'Type at least 3 letters of the supplier name.';
    }
    if (option === 'amount' && isNaN(text)) {
        return 'Enter an amount, for example 1426.00.';
    }
    return '';
}

// filters: the ticked status chips; none ticked is 'All'
export function defaultListState(tab) {
    return { search: '', filters: [], sort: TABS[tab].defaultSort };
}

// card and cardIcon: the card's status band, where the label is kept short (the full
// status is its tooltip)
export const STATUS_META = {
    awaiting: { pill: 'AWAITING YOUR APPROVAL', short: 'Awaiting you', icon: 'far fa-clock', card: 'Awaiting', cardIcon: 'fas fa-clock' },
    // On the property's list, but the user is not one of its approvers: view only
    others: { pill: 'AWAITING OTHER APPROVERS', short: 'Not yours', icon: 'fas fa-user-friends', card: 'Not yours', cardIcon: 'fas fa-user-friends' },
    queried: { pill: 'QUERIED', short: 'Queried', icon: 'fas fa-question-circle', card: 'Queried', cardIcon: 'fas fa-question-circle' },
    approved: { pill: 'APPROVED BY YOU', short: 'Approved', icon: 'fas fa-check-circle', card: 'You approved', cardIcon: 'fas fa-check' },
    paid: { pill: 'PAID', short: 'Paid', icon: 'fas fa-check-double', card: 'Paid', cardIcon: 'fas fa-check-double' }
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "02 Sep 2026", as the date filter's 'dd MMM yyyy' shows it
export function formatDate(value) {
    const date = parseServerTime(value);
    if (!value || isNaN(date.getTime())) {
        return '';
    }
    return `${('0' + date.getDate()).slice(-2)} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

// PaymentApprovalStatus: 1 waiting, 2 queried, 3 approved
export const APPROVER_WAITING = 1;
export const APPROVER_APPROVED = 3;
const APPROVER_STATUS = { [APPROVER_WAITING]: 'not_actioned', 2: 'queried', [APPROVER_APPROVED]: 'approved' };

/**
 * Which of a payment's approvers is the current user. The API marks the user's own row
 * (IsCurrentUser); before it did, friendly names were all there was to go on, so they are
 * still the fallback.
 * @param {Array<Object>} approvers The payment's Approvers (WebPaymentApprover)
 * @param {function(string): boolean} isMe Whether a friendly name is the current user's
 * @return {function(Object): boolean} Whether an approver is the current user
 */
export function currentApproverMatcher(approvers, isMe) {
    const serverKnowsUser = (approvers || []).some(approver => typeof approver.IsCurrentUser === 'boolean');
    return approver => (serverKnowsUser
        ? !!approver.IsCurrentUser
        : isMe((approver.ApproverFriendlyName || '').trim()));
}

export const APPROVER_STATUS_META = {
    approved: { label: 'Approved', icon: 'fas fa-check-circle' },
    queried: { label: 'Queried', icon: 'fas fa-question-circle' },
    not_actioned: { label: 'Not actioned', icon: 'fas fa-clock' }
};

// GLAccountBalanceAmount is set to 1 by the API when the account has no budget
const NO_BUDGET_LOADED = 1;

// The server's times ("2026-09-13T13:56:46.847", DateTime.Now on the server) are South African
// clock time with no zone. Read as that clock time whatever the phone's own zone (new Date()
// would shift them by the phone's offset), and parsed here rather than by new Date(), which
// older Safari refuses for some fractions of a second (".4", seven digits). A time with a zone
// (a message sent from this page, toISOString()) is an instant, read as such.
const SERVER_TIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:?\d{2})?$/;
const SA_OFFSET_MS = 2 * 60 * 60 * 1000;

export function parseServerTime(value) {
    if (value instanceof Date) {
        return value;
    }
    if (typeof value !== 'string') {
        return new Date(NaN);
    }
    // WCF's JSON form: "/Date(1757750206847)/" or "/Date(1757750206847+0200)/"
    const wcf = /^\/Date\((-?\d+)([+-]\d{4})?\)\/$/.exec(value);
    if (wcf) {
        return new Date(Number(wcf[1]));
    }
    const match = SERVER_TIME.exec(value.trim());
    if (!match) {
        return new Date(value);
    }
    const ms = match[7] ? Number(`0.${match[7]}`) * 1000 : 0;
    const parts = [Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), Number(match[6] || 0), Math.round(ms)];
    if (match[8]) {
        // An instant: UTC from the parts, then the zone's offset taken off
        const utc = Date.UTC(...parts);
        if (match[8] === 'Z') {
            return new Date(utc);
        }
        const sign = match[8][0] === '-' ? -1 : 1;
        const offset = match[8].slice(1).replace(':', '');
        return new Date(utc - sign * (Number(offset.slice(0, 2)) * 60 + Number(offset.slice(2))) * 60000);
    }
    // South African clock time (UTC+2, no daylight saving): the same instant on every phone
    return new Date(Date.UTC(...parts) - SA_OFFSET_MS);
}

// "Today, 15:39", "Yesterday, 09:26", "11 Sep, 21:26", or with the year if not this one:
// 24-hour time, in the phone's own clock
export function formatMessageTime(value, now) {
    const date = parseServerTime(value);
    if (!value || isNaN(date.getTime())) {
        return '';
    }
    const time = `${('0' + date.getHours()).slice(-2)}:${('0' + date.getMinutes()).slice(-2)}`;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const daysAgo = Math.round((today - day) / 86400000);
    if (daysAgo === 0) {
        return `Today, ${time}`;
    }
    if (daysAgo === 1) {
        return `Yesterday, ${time}`;
    }
    const year = date.getFullYear() === now.getFullYear() ? '' : ` ${date.getFullYear()}`;
    return `${date.getDate()} ${MONTHS[date.getMonth()]}${year}, ${time}`;
}

export function normaliseName(name) {
    return (name || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
}

export function createIdentity(names) {
    const known = (names || []).map(normaliseName).filter(Boolean);
    return (name) => {
        const candidate = normaliseName(name);
        return !!candidate && known.indexOf(candidate) > -1;
    };
}

// Names can carry a tag, e.g. "Sibusiso Mahlangu (U28)": only words that start with a
// letter count
export function initials(name) {
    const words = (name || '')
        .replace(/\([^)]*\)/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(word => /^[A-Za-zÀ-ɏ]/.test(word));
    if (words.length === 0) {
        return '?';
    }
    const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
    return (words[0].charAt(0) + last).toUpperCase();
}

// Same output as Angular's number filter at 2 places, with the R prefix: "R 1,426.00". The
// space is a non-breaking one, so the R never ends a line on its own.
export function formatAmount(amount) {
    const value = Number(amount) || 0;
    const fixed = Math.abs(value).toFixed(2);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `R ${value < 0 ? '-' : ''}${whole}.${parts[1]}`;
}

// South African style, as the Details page shows amounts: "R 1 875.00", spaces (non-breaking,
// so an amount never splits over two lines) between the thousands
export function formatAmountSA(amount) {
    const value = Number(amount) || 0;
    const parts = Math.abs(value).toFixed(2).split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `R ${value < 0 ? '-' : ''}${whole}.${parts[1]}`;
}

// The budget line under an allocation on Details. Nothing loaded, or nothing left (none, or
// overspent), is the error colour. Without a balance at all (payments from the payment
// search do not carry one) there is no line.
export function budgetFor(allocation) {
    const balance = allocation.GLAccountBalanceAmount;
    if (balance === undefined || balance === null) {
        return null;
    }
    if (balance === NO_BUDGET_LOADED) {
        return { label: 'No budget loaded', isWarning: true };
    }
    return {
        label: balance < 0 ? `${formatAmountSA(-balance)} over budget` : `${formatAmountSA(balance)} remaining`,
        isWarning: balance <= 0
    };
}

const DOCUMENT_TYPES = {
    pdf: { icon: 'far fa-file-pdf', label: 'PDF document' },
    xlsx: { icon: 'far fa-file-excel', label: 'Excel spreadsheet' },
    xls: { icon: 'far fa-file-excel', label: 'Excel spreadsheet' },
    csv: { icon: 'far fa-file-excel', label: 'Excel spreadsheet' },
    docx: { icon: 'far fa-file-word', label: 'Word document' },
    doc: { icon: 'far fa-file-word', label: 'Word document' },
    jpg: { icon: 'far fa-file-image', label: 'JPG image' },
    jpeg: { icon: 'far fa-file-image', label: 'JPG image' },
    png: { icon: 'far fa-file-image', label: 'PNG image' },
    zip: { icon: 'far fa-file-archive', label: 'ZIP archive' }
};

// The icon and type line for a document, from its extension (".pdf" or "pdf")
export function documentType(extension) {
    const ext = (extension || '').replace('.', '').trim().toLowerCase();
    return DOCUMENT_TYPES[ext] || { icon: 'far fa-file', label: ext ? `${ext.toUpperCase()} file` : 'File' };
}

// "Sibusiso Mahlangu (U28)": the name, and the tag in brackets shown after it in muted text
export function splitNameSuffix(name) {
    const match = /^(.*?)\s*(\([^)]*\))\s*$/.exec(name || '');
    return match ? { name: match[1], suffix: match[2] } : { name: name || '', suffix: '' };
}

function toTime(value) {
    const time = parseServerTime(value).getTime();
    return isNaN(time) ? 0 : time;
}

/**
 * @param {Object} payment A WebPaymentApproval
 * @param {Object} context
 * @param {function(string): boolean} context.isMe Whether a friendly name is the current user's
 * @param {boolean} [context.queriedThisSession] The user sent a query on this payment in this session
 * @param {Array<Object>} [context.pendingMessages] Messages sent but not yet in the API's data
 * @param {Object<string, string>} [context.messageKeyAliases] Server message key: the key it had while being sent
 */
export function toApprovalViewModel(payment, context) {
    const isMe = context.isMe;
    // The API marks the user's own approver row (IsCurrentUser). Before it did, friendly
    // names were all there was to go on, so they are still the fallback.
    const serverKnowsUser = (payment.Approvers || []).some(approver => typeof approver.IsCurrentUser === 'boolean');
    const isCurrentApprover = currentApproverMatcher(payment.Approvers, isMe);

    const approvers = (payment.Approvers || []).map((approver) => {
        const name = (approver.ApproverFriendlyName || '').trim();
        const status = APPROVER_STATUS[approver.ApproverStatus] || 'not_actioned';
        const parts = splitNameSuffix(name);
        return {
            name,
            // Details shows the name with its tag, e.g. "(U28)", after it in muted text
            displayName: parts.name,
            nameSuffix: parts.suffix,
            status,
            statusLabel: APPROVER_STATUS_META[status].label,
            statusIcon: APPROVER_STATUS_META[status].icon,
            initials: initials(name),
            isMandatory: !!approver.IsMandatory,
            isCurrentUser: isCurrentApprover(approver)
        };
    });

    const currentApprover = approvers.find(approver => approver.isCurrentUser);
    // Whether the user may approve or query it at all. A property's list can hold payments
    // the user is not an approver on; approving those did nothing (and said it had).
    // Without the API's flag, assume so, as before.
    const canApprove = serverKnowsUser ? !!currentApprover : true;
    let currentUserAction = null;
    if (payment.HasApproved) {
        currentUserAction = 'approved';
    } else if (context.queriedThisSession || (currentApprover && currentApprover.status === 'queried')) {
        currentUserAction = 'queried';
    }

    // Progress: every required approver, plus any N of the discretionary ones
    const mandatory = approvers.filter(approver => approver.isMandatory);
    const discretionary = approvers.filter(approver => !approver.isMandatory);
    const discretionaryRequired = Number(payment.NumberOfDiscretionaryApprovers) || 0;
    const approvedMandatory = mandatory.filter(approver => approver.status === 'approved').length;
    const approvedDiscretionary = discretionary.filter(approver => approver.status === 'approved').length;
    const requiredApprovals = mandatory.length + discretionaryRequired;
    const approvedCount = Math.min(
        approvedMandatory + Math.min(approvedDiscretionary, discretionaryRequired),
        requiredApprovals
    );
    const approvalsNeeded = requiredApprovals - approvedCount;

    const queriedApprover = approvers.find(approver => approver.status === 'queried');
    const paidOn = payment.DatePaid || null;
    // Queried only while an approver is querying it. The workflow's own status stayed
    // Queried after the query was answered, so it is not used.
    let status = canApprove ? 'awaiting' : 'others';
    if (paidOn) {
        status = 'paid';
    } else if (currentUserAction === 'approved') {
        status = 'approved';
    } else if (currentUserAction === 'queried' || queriedApprover) {
        status = 'queried';
    }

    let queriedBy = '';
    if (status === 'queried') {
        if (currentUserAction === 'queried' || (queriedApprover && queriedApprover.isCurrentUser)) {
            queriedBy = 'you';
        } else if (queriedApprover) {
            queriedBy = queriedApprover.name;
        }
    }

    const longLabels = {
        awaiting: 'Awaiting your approval',
        others: 'Awaiting other approvers',
        queried: queriedBy ? `Queried by ${queriedBy}` : 'Queried',
        approved: 'Approved by you',
        paid: `Paid on ${formatDate(paidOn)}`
    };

    // key: stable across reloads, so the thread keeps its bubbles (and does not replay
    // their entrance) when the list is refreshed. Two comments can share a time and author,
    // hence the count.
    const seenKeys = {};
    const serverMessages = (payment.Comment || [])
        .map(comment => ({
            key: `${comment.WhenCommented}|${comment.WhoComment}`,
            author: comment.WhoComment,
            initials: initials(comment.WhoComment),
            isCurrentUser: isMe(comment.WhoComment),
            sentAt: comment.WhenCommented,
            text: comment.Comment,
            state: 'sent'
        }))
        .sort((a, b) => toTime(a.sentAt) - toTime(b.sentAt));
    const keyAliases = context.messageKeyAliases || {};
    serverMessages.forEach((message) => {
        const count = seenKeys[message.key] = (seenKeys[message.key] || 0) + 1;
        if (count > 1) {
            message.key += `#${count}`;
        }
        // A message the user sent here keeps the key it had while sending, so its bubble
        // stays put when the server's copy takes over (it flashed in again)
        message.key = keyAliases[message.key] || message.key;
    });
    const now = context.now || new Date();
    const messages = serverMessages.concat(context.pendingMessages || []).map(message => Object.assign(message, {
        timeLabel: formatMessageTime(message.sentAt, now)
    }));

    const allocations = (payment.Allocations || []).map(allocation => ({
        name: allocation.AccountName || '',
        code: allocation.AccountNumber || '',
        amount: Number(allocation.Amount) || 0,
        amountLabel: formatAmountSA(allocation.Amount),
        budget: budgetFor(allocation)
    }));
    const firstAllocation = allocations[0];
    let expenseSummary = firstAllocation ? firstAllocation.name : '';
    if (allocations.length > 1) {
        expenseSummary += ` +${allocations.length - 1} more`;
    }

    const documents = (payment.Attachments || []).map((attachment, index) => ({
        id: `${payment.PaymentId}-${index}`,
        name: attachment.Name,
        fileLabel: documentType(attachment.DocumentExtension).label,
        fileIcon: documentType(attachment.DocumentExtension).icon,
        fileName: `${attachment.Name}${attachment.DocumentExtension || ''}`,
        url: attachment.Path
    }));

    const discretionaryCaption = `any ${discretionaryRequired} discretionary ${discretionaryRequired === 1 ? 'approver' : 'approvers'}`;
    let approversCaption = '';
    if (mandatory.length > 0 && discretionaryRequired > 0) {
        approversCaption = `All required approvers, plus ${discretionaryCaption}`;
    } else if (discretionaryRequired > 0) {
        approversCaption = discretionaryCaption.charAt(0).toUpperCase() + discretionaryCaption.slice(1);
    } else if (mandatory.length > 0) {
        approversCaption = 'All required approvers';
    }

    const progressShort = `${approvedCount} of ${requiredApprovals}`;
    const segments = [];
    for (let i = 0; i < requiredApprovals; i++) {
        segments.push(i < approvedCount);
    }

    // The status at the right of the Details top bar, in a light tint for the navy:
    // awaiting (the user's or others'), queried, approved (by the user, or by all), paid
    // Every approval it needs is in: the workflow says so (PaymentApprovalStatus 3), or the
    // user's approval, just made here, was the last one (before the reloaded list has it).
    // A query holds it back, as the server's approval rules do.
    const isFullyApproved = payment.PaymentApprovalStatus === 3
        || (currentUserAction === 'approved' && requiredApprovals > 0 && approvalsNeeded === 0 && !queriedApprover);
    let topStatus = { tone: 'awaiting', icon: 'fas fa-clock', label: 'Awaiting' };
    if (status === 'paid') {
        topStatus = { tone: 'approved', icon: 'fas fa-check-double', label: `Paid · ${formatDate(paidOn)}` };
    } else if (status === 'approved' || isFullyApproved) {
        topStatus = { tone: 'approved', icon: 'fas fa-check-circle', label: 'Approved' };
    } else if (status === 'queried') {
        topStatus = { tone: 'queried', icon: 'fas fa-question-circle', label: 'Queried' };
    }

    return {
        id: payment.PaymentId,
        supplier: payment.ServiceProviderName || '',
        invoiceNumber: payment.InvoiceNumber || '',
        invoiceDate: payment.DateCaptured,
        invoiceTime: toTime(payment.DateCaptured),
        // History's "Recently actioned": when it was paid, else approved, else captured
        actionTime: toTime(paidOn || payment.DateApproved || payment.DateCaptured),
        paidOn,
        amount: Number(payment.Amount) || 0,
        amountLabel: formatAmount(payment.Amount),
        // Details uses South African formatting: "R 1 875.00"
        amountLabelSA: formatAmountSA(payment.Amount),
        topStatus,
        allocations,
        expenseSummary,
        notes: payment.Notes || '',
        futurePaymentDate: payment.FuturePaymentDate || null,
        approvers,
        mandatoryApprovers: mandatory,
        discretionaryApprovers: discretionary,
        discretionaryRequired,
        approversCaption,
        // The Approvers card's groups on Details, each only when it has members
        approverGroups: [
            { key: 'required', label: 'Required', note: 'All must approve', approvers: mandatory },
            { key: 'discretionary', label: 'Discretionary', note: `Any ${discretionaryRequired} of ${discretionary.length}`, approvers: discretionary }
        ].filter(group => group.approvers.length > 0),
        // The Approvers header's count: "2 of 2 approved", or "None assigned"
        approversCountLabel: requiredApprovals > 0 ? `${approvedCount} of ${requiredApprovals} approved` : 'None assigned',
        requiredApprovals,
        approvedCount,
        approvalsNeeded,
        segments,
        progressShort,
        progressTitle: `${approvedCount} of ${requiredApprovals} approved`,
        progressLong: `${approvedCount} of ${requiredApprovals} approved${approvalsNeeded > 0 ? ` · needs ${approvalsNeeded} more` : ''}`,
        currentUserAction,
        isActioned: currentUserAction !== null,
        canApprove,
        // Every approval it needs is in (it goes on to be paid): an approval can no longer
        // be cancelled. PaymentApprovalStatus 3 is the workflow's Approved.
        isFullyApproved,
        status,
        statusMeta: STATUS_META[status],
        pillLabel: status === 'paid' ? `PAID · ${formatDate(paidOn).toUpperCase()}` : STATUS_META[status].pill,
        statusLong: longLabels[status],
        queriedBy,
        messages,
        messageCount: messages.length,
        messagesTitle: `${messages.length} ${messages.length === 1 ? 'query' : 'queries'}`,
        // The newest message from someone else, for the card's unread dot
        latestOtherMessageTime: serverMessages
            .filter(message => !message.isCurrentUser)
            .reduce((latest, message) => Math.max(latest, toTime(message.sentAt)), 0),
        documents
    };
}

export function matchesSearch(item, search) {
    const term = normaliseName(search);
    if (!term) {
        return true;
    }
    return normaliseName(item.supplier).indexOf(term) > -1 || normaliseName(item.invoiceNumber).indexOf(term) > -1;
}

// statusFilter: one status ('all' for any), or the ticked statuses (none ticked is any)
export function matchesStatus(item, statusFilter) {
    if (Array.isArray(statusFilter)) {
        return statusFilter.length === 0 || statusFilter.indexOf(item.status) > -1;
    }
    return !statusFilter || statusFilter === 'all' || item.status === statusFilter;
}

export function sortItems(items, sort) {
    const sorted = items.slice();
    const comparers = {
        newest: (a, b) => b.invoiceTime - a.invoiceTime,
        oldest: (a, b) => a.invoiceTime - b.invoiceTime,
        'amount-desc': (a, b) => b.amount - a.amount,
        'amount-asc': (a, b) => a.amount - b.amount,
        recent: (a, b) => b.actionTime - a.actionTime
    };
    return sorted.sort(comparers[sort] || comparers.newest);
}
